import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'
import _ from 'lodash'
import DTReact from 'datatables.net-react'
import DT from 'datatables.net-dt'
import 'datatables.net-responsive-dt'
import AjaxActions from '../actions/AjaxActions'

// Register DT core with the React wrapper once at module load
DTReact.use(DT)

const DataTable = forwardRef(function DataTable(props, ref) {
  const {
    children,
    paginationLength,
    options = {},
    searchPlaceholder,
    filter,
    detailsFormat,
    columnsHide,
    ...tableProps
  } = props

  // dtRef exposes .dt() → the DataTables API instance
  const dtRef = useRef(null)
  const wrapperRef = useRef(null)
  // Track last displayStart to avoid redundant page jumps
  const prevDisplayStartRef = useRef(options.displayStart)

  // Expose cachedDatatable for backward compat with class components that do:
  //   ref={node => { this.cachedDatatable = node && node.cachedDatatable }}
  useImperativeHandle(ref, () => ({
    get cachedDatatable() {
      return dtRef.current?.dt() ?? null
    },
  }))

  // --- Build processed options (read once by DTReact at mount) ---

  let toolbar = ''
  if (options.buttons) toolbar += 'B'
  if (paginationLength) toolbar += 'l'
  if (columnsHide) toolbar += 'C'

  // Wrap string AJAX URL to fire AjaxActions side-effect on each XHR
  let ajaxOption = options.ajax
  if (typeof ajaxOption === 'string') {
    const url = ajaxOption
    ajaxOption = {
      url,
      complete: xhr => AjaxActions.contentLoaded(xhr),
    }
  }

  // Merge defaults over caller options. DTReact reads this object once at mount
  // so post-mount changes to options (e.g. displayStart) are handled via DT API.
  const processedOptions = {
    ...options,
    ajax: ajaxOption,
    dom:
      "<'dt-toolbar'<'col-xs-12 col-sm-6'f><'col-sm-6 col-xs-12 hidden-xs text-right'" +
      toolbar +
      '>r>' +
      't' +
      "<'dt-toolbar-footer'<'col-sm-6 col-xs-12 hidden-xs'i><'col-xs-12 col-sm-6'p>>",
    language: {
      search:
        "<span class='input-group-addon input-sm'><i class='glyphicon glyphicon-search'></i></span> ",
      lengthMenu: '_MENU_',
      searchPlaceholder: 'Type to search...',
    },
    autoWidth: false,
    responsive: options.responsive !== undefined ? options.responsive : true,
  }

  // --- Helpers ---

  const setSearchPlaceholder = useCallback(() => {
    const dt = dtRef.current?.dt()
    if (!dt) return
    $(dt.table().container())
      .find('.dt-search input[type="search"]')
      .attr('placeholder', searchPlaceholder || '')
  }, [searchPlaceholder])

  // --- Effects ---

  // 1. Mount — runs AFTER DTReact's init effect (React runs child effects before
  //    parent effects, so dtRef.current.dt() is guaranteed non-null here).
  //    Attaches filter/detailsFormat handlers and sets initial placeholder.
  useEffect(() => {
    const dt = dtRef.current?.dt()
    if (!dt) return

    setSearchPlaceholder()

    // Inject a custom clear (×) button next to the search input
    const $container = $(dt.table().container())
    const $searchInput = $container.find('.dt-search input[type="search"]')
    if ($searchInput.length && !$searchInput.next('.dt-search-clear-btn').length) {
      const $clearBtn = $('<button type="button" class="dt-search-clear-btn" aria-label="Clear search">&times;</button>')
      $clearBtn.hide()
      $searchInput.after($clearBtn)
      $searchInput.on('input.dtclear keyup.dtclear', function () {
        $clearBtn.toggle(this.value.length > 0)
      })
      if ($searchInput.val()) $clearBtn.show()
      $clearBtn.on('click.dtclear', function () {
        $searchInput.val('')
        dt.search('').draw()
        $clearBtn.hide()
        $searchInput.trigger('focus')
      })
    }

    if (filter) {
      $(dt.table().node()).on(
        'keyup.dtfilter change.dtfilter',
        'thead th input[type=text]',
        function () {
          dt
            .column($(this).parent().index() + ':visible')
            .search(this.value)
            .draw()
        }
      )
    }

    if (detailsFormat) {
      $(dt.table().node()).on('click.dtdetails', 'td.details-control', function () {
        const tr = $(this).closest('tr')
        const row = dt.row(tr)
        if (row.child.isShown()) {
          row.child.hide()
          tr.removeClass('shown')
        } else {
          row.child(detailsFormat(row.data())).show()
          tr.addClass('shown')
        }
      })
    }

    return () => {
      const node = dt.table().node()
      if (node) $(node).off('.dtfilter').off('.dtdetails')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // 2. ResizeObserver — re-lay-out the columns when the container's WIDTH
  //    changes (sidebar collapse, window resize).
  //
  //    Width only. The observed wrapper also grows and shrinks with its own
  //    content: every server-side draw swaps in a page of rows with different
  //    heights, and each image that finishes loading grows its row again. Acting
  //    on those meant a full measure-and-relayout fired ~150ms *after* the new
  //    page had already painted, so one pagination click moved the table twice —
  //    the flicker this guard exists to prevent. DataTables core avoids the same
  //    trap by hanging its observer on a zero-height sizer div ("so it isn't
  //    impacted by height changes", dataTables.js), but core installs that from
  //    inside _fnCalculateColumnWidths, which returns early under the
  //    `autoWidth: false` every list here passes — so the job falls to us.
  //
  //    columns.adjust() is the whole recalc: it fires DataTables' `column-sizing`
  //    event, which Responsive answers with a full _resizeAuto()/_resize() pass.
  //    Chaining .responsive.recalc() onto it repeated that identical
  //    clone-measure-reflow a second time on every single resize.
  useEffect(() => {
    const wrapper = wrapperRef.current
    let lastWidth = wrapper ? wrapper.clientWidth : 0

    const recalc = _.debounce(() => {
      const dt = dtRef.current?.dt()
      if (!dt || !wrapper) return

      const width = wrapper.clientWidth
      if (width === lastWidth) return
      lastWidth = width

      dt.columns.adjust()
    }, 150)

    let observer = null
    if (typeof ResizeObserver !== 'undefined' && wrapper) {
      observer = new ResizeObserver(recalc)
      observer.observe(wrapper)
    }

    window.addEventListener('resize', recalc)

    return () => {
      window.removeEventListener('resize', recalc)
      if (observer) observer.disconnect()
      recalc.cancel()
    }
  }, [])

  // 3. Pagination — handle displayStart URL param changes via DT API.
  //    No re-init needed; server-side AJAX fetches the right data on draw.
  useEffect(() => {
    const dt = dtRef.current?.dt()
    if (!dt) return

    const displayStart = options.displayStart
    if (displayStart === prevDisplayStartRef.current) return
    prevDisplayStartRef.current = displayStart

    const pageInfo = dt.page.info()
    if (!pageInfo.length) return

    const nextPage = Math.round((displayStart ?? 0) / pageInfo.length)
    if (nextPage !== pageInfo.page) {
      dt.page(nextPage).draw(false)
    }
  })

  // 4. Search placeholder — update when searchPlaceholder prop changes.
  useEffect(() => {
    setSearchPlaceholder()
  }, [setSearchPlaceholder])

  return (
    <div ref={wrapperRef}>
      <DTReact ref={dtRef} {...tableProps} options={processedOptions}>
        {children}
      </DTReact>
    </div>
  )
})

export default DataTable
